/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import {
  withLoading,
  useTranslator,
  MarkdownRenderer,
  useAppContext,
  cloudflareImage,
} from '@front-activerse/common-components';
import Masonry from 'react-masonry-css';
import { Link, NavLink } from 'react-router-dom';
import { Container, Card, CardBody, Nav, NavItem, Button, Row, Col } from 'reactstrap';
import truncateMarkdown from 'markdown-truncate';
import { withRouter } from '@front-activerse/common-components';

import Layout from '../../Layout/Layout';
import Loader from '../../Layout/Loader';
import TitleBar from '../../Layout/TitleBar';
import ArticleListContainerPlaceholder from '../ArticleList/ArticleListContainerPlaceholder';

const ArticleList = ({
  articleList,
  category,
  categories,
  articlePagination,
  loadArticle,
  loadArticleList,
  articlesPerPage,
  truncateLimit,
  categoryAsNumber,
  router,
}) => {
  const { t } = useTranslator();

  const prepareState = () => {
    const pageNumber = {};
    const articleList = {};

    categories.forEach((cat) => {
      pageNumber[cat] = 1;
      articleList[cat] = [];
    });

    return {
      pageNumber,
      articleList,
    };
  };

  const [isLoading, setIsLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(prepareState().pageNumber);
  const [tempArticleList, setArticleList] = useState(prepareState().articleList);

  const {
    consts: { APP_BRAND },
  } = useAppContext();

  useEffect(() => {
    if (tempArticleList[category].length === 0) {
      setArticleList({
        ...tempArticleList,
        [category]: articleList,
      });
    }

    router.context.trackFirebaseEvent('screen_view', {
      firebase_screen: 'Feed',
      feed_category: categoryAsNumber[category],
    });
  }, []);

  useEffect(() => {
    if (tempArticleList[category].length === 0) {
      setArticleList({
        ...tempArticleList,
        [category]: articleList,
      });
    }
  }, [category]);

  const loadMore = async () => {
    setIsLoading(true);

    const articleList = await loadArticleList({
      variables: {
        categoryIdentifier: category === 'all' ? null : category,
        pageNumber: pageNumber[category] + 1,
        articlesPerPage,
      },
    });

    setIsLoading(false);
    setArticleList({
      ...tempArticleList,
      [category]: [...tempArticleList[category], ...articleList.data.article],
    });
    setPageNumber({
      ...pageNumber,
      [category]: pageNumber[category] + 1,
    });
  };

  const fetchMore = async () => {
    await loadArticleList({
      variables: {
        categoryIdentifier: category === 'all' ? null : category,
        pageNumber: pageNumber[category] + 1,
        articlesPerPage,
      },
    });
  };

  const renderFilters = () => {
    return (
      <div className="sub-navigation">
        <Container>
          <Nav className="d-block">
            {categories.map((cat) => (
              <NavItem key={cat}>
                <NavLink
                  to={`/knowledge/${cat}`}
                  replace={true}
                  className="nav-link"
                  onClick={() =>
                    category !== cat &&
                    router.context.trackFirebaseEvent('screen_view', {
                      firebase_screen: 'Feed',
                      feed_category: categoryAsNumber[category],
                    })
                  }
                >
                  {t(`feed.submenuItem.${cat}`)}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </Container>
      </div>
    );
  };

  const renderTeaser = (article) => {
    if (article.teaser.markdown !== '') {
      const content = article.teaser.html.replace(/(<([^>]+)>)/gi, '');
      return (
        <div className="article-teaser">
          <MarkdownRenderer
            content={truncateMarkdown(content, {
              limit: truncateLimit,
              ellipsis: true,
            })}
            fromList
          />

          <Button color="link" size="sm" state={{ fromArticleList: true }} className="px-0 py-0">
            <b>{t('feedCard.readMore.button')}</b>
          </Button>
        </div>
      );
    }

    return '';
  };

  const renderArticleList = () => {
    return tempArticleList[category].map((article) => {
      const coverImage = cloudflareImage(article.thumbUrl, `${APP_BRAND}`, 'articleThumbnail');

      return (
        <Card
          key={article.id}
          tag={Link}
          to={{
            pathname: `/knowledge/${category}/${article.id}-${article.slug}`,
          }}
          state={{ fromArticleList: true }}
          onMouseEnter={() => {
            loadArticle({
              variables: { articleId: article.id },
            });
          }}
          onTouchStart={() => {
            loadArticle({
              variables: { articleId: article.id },
            });
          }}
        >
          <div className="thumbImg">
            <img src={coverImage} className="img-fluid" alt="" />
          </div>
          <CardBody>
            <div>
              <span
                className="category"
                style={{
                  color: `#${article.category.label.color}`,
                  backgroundColor: `#${article.category.label.bgColor}`,
                }}
              >
                {article.category.name}
              </span>
            </div>
            <h2 className="title-medium semi-bold text-text-main-color my-m">{article.title}</h2>
            {renderTeaser(article)}
          </CardBody>
        </Card>
      );
    });
  };

  const breakpointColumnsObj = {
    default: 3,
    992: 2,
    500: 1,
  };

  return (
    <Layout>
      <TitleBar showBackBtn={false}>{t('feed.navbarTitle')}</TitleBar>
      <div className="page-template-knowledge">
        <Container className="py-md-4">
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                {renderFilters()}
                <div className="article-list-container">
                  <Masonry
                    breakpointCols={breakpointColumnsObj}
                    className="my-masonry-grid"
                    columnClassName="my-masonry-grid_column"
                  >
                    {tempArticleList[category].length > 0 && renderArticleList()}
                  </Masonry>

                  {tempArticleList[category].length === 0 && (
                    <Row>
                      <Col md={{ size: 6, offset: 3 }}>
                        <p className="my-5 text-center">{t('feed.noResults')}</p>
                      </Col>
                    </Row>
                  )}

                  {articlePagination.pageCount > pageNumber[category] ? (
                    <section className="text-center pt-3">
                      <Button
                        outline
                        color="secondary"
                        onClick={() => loadMore()}
                        onMouseEnter={() => fetchMore()}
                        onTouchStart={() => fetchMore()}
                      >
                        {t('feed.moreArticles.button')}
                      </Button>
                    </section>
                  ) : (
                    ''
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>

        {isLoading ? <Loader /> : null}
      </div>
    </Layout>
  );
};

const Placeholder = ({ categories }) => <ArticleListContainerPlaceholder categories={categories} />;

export default withLoading(withRouter(ArticleList), Placeholder);
